import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, RefreshCw, Pencil, ExternalLink, Star } from "lucide-react";
import { adminListTools, adminListQuickPicks } from "@/admin/cmsApi";

export default function AdminToolsList() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [quickPicks, setQuickPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, qp] = await Promise.all([adminListTools(), adminListQuickPicks()]);
      setItems(t.items || []);
      setCategories(t.categories || []);
      setQuickPicks(qp.items || []);
    } catch (e) {
      setError(e.message || "Failed to load tools.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const quickPickSlugs = useMemo(
    () => new Set(quickPicks.map((p) => p.tool_slug)),
    [quickPicks]
  );

  const filtered = useMemo(() => {
    const qNorm = q.trim().toLowerCase();
    return items.filter((t) => {
      if (cat && !(t.categories || []).includes(cat)) return false;
      if (qNorm && !(
        t.name.toLowerCase().includes(qNorm) ||
        t.slug.toLowerCase().includes(qNorm) ||
        (t.desc?.en || "").toLowerCase().includes(qNorm) ||
        (t.desc?.ar || "").includes(q.trim())
      )) return false;
      return true;
    });
  }, [items, q, cat]);

  const categoryOptions = useMemo(
    () => [{ value: "", label: `All categories (${items.length})` }]
      .concat(categories.map((c) => ({
        value: c.id,
        label: `${c.label?.en || c.id} (${items.filter((t) => (t.categories || []).includes(c.id)).length})`,
      }))),
    [categories, items]
  );

  return (
    <div data-testid="admin-tools-list">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="sas-eyebrow mb-2">Content</div>
          <h1 className="sas-h1">Tools</h1>
          <p className="sas-body mt-3 max-w-2xl text-[15px]">
            Create, edit, reorder, and delete the tools shown on the public
            <code className="mx-1 px-1 py-0.5 rounded bg-[color:var(--sas-accent-soft)] text-[12px]">/tools</code>
            directory. Flag any tool as a “quick pick” to surface it in the Start-Here section.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
            data-testid="admin-tools-refresh"
          >
            <RefreshCw size={14} aria-hidden /> Refresh
          </button>
          <Link
            to="/admin/tools/quick-picks"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
            data-testid="admin-tools-manage-quick-picks"
          >
            <Star size={14} aria-hidden /> Quick picks
          </Link>
          <button
            type="button"
            onClick={() => navigate("/admin/tools/new")}
            className="sas-btn sas-btn-primary"
            data-testid="admin-tools-new"
          >
            <Plus size={16} aria-hidden /> New tool
          </button>
        </div>
      </div>

      {/* filters */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3">
        <div className="relative">
          <Search size={14} aria-hidden className="absolute top-1/2 -translate-y-1/2 start-3 text-[color:var(--sas-ink-2)]" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tools by name, slug, or description…"
            className="sas-input ps-9"
            data-testid="admin-tools-search"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="sas-input"
          data-testid="admin-tools-category-filter"
        >
          {categoryOptions.map((o) => (
            <option key={o.value || "all"} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* table */}
      <div className="mt-6">
        {error ? (
          <div className="rounded-xl border border-[color:#fecaca] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#991b1b]" role="alert" data-testid="admin-tools-error">
            {error}
          </div>
        ) : loading ? (
          <div className="space-y-2" data-testid="admin-tools-loading">
            {[0,1,2,3,4,5].map((i) => (
              <div key={i} className="h-14 rounded-lg bg-white border border-[color:var(--sas-line)] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[color:var(--sas-line)] rounded-xl py-16 text-center" data-testid="admin-tools-empty">
            <h2 className="sas-h3">No tools match</h2>
            <p className="sas-body mt-2 text-[14px]">Try clearing the filter, or create a new tool.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--sas-line)] border border-[color:var(--sas-line)] rounded-xl overflow-hidden bg-white" data-testid="admin-tools-rows">
            {filtered.map((t) => {
              const catLabels = (t.categories || [])
                .map((id) => categories.find((c) => c.id === id)?.label?.en || id)
                .join(" · ");
              const isQuick = quickPickSlugs.has(t.slug);
              return (
                <li key={t.id} data-testid={`admin-tool-row-${t.id}`}>
                  <div className="flex items-start gap-4 px-4 py-3.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[14px] font-semibold text-[color:var(--sas-ink)]">{t.name}</span>
                        {isQuick && (
                          <span
                            className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded-full bg-[color:var(--sas-accent-soft)] text-[color:var(--sas-accent)] font-medium"
                            data-testid={`admin-tool-quick-${t.slug}`}
                          >
                            <Star size={10} aria-hidden /> Quick pick
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-[color:var(--sas-ink-2)] truncate" dir="ltr">
                        {t.slug} · {catLabels}
                      </div>
                      <div className="mt-1 text-[12.5px] text-[color:var(--sas-ink-2)] line-clamp-2" dir="ltr">
                        {(t.desc?.en || t.desc?.ar || "").trim()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11.5px] text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] underline-offset-4 hover:underline"
                        data-testid={`admin-tool-visit-${t.slug}`}
                      >
                        Visit <ExternalLink size={11} aria-hidden />
                      </a>
                      <Link
                        to={`/admin/tools/${t.id}`}
                        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--sas-ink)] px-2.5 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)]"
                        data-testid={`admin-tool-edit-${t.slug}`}
                      >
                        <Pencil size={12} aria-hidden /> Edit
                      </Link>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
